import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import Modal from "@via/components/modal.tsx";
import Table from "@via/components/table/table.tsx";
import { EMPTY_TABLE_MESSAGE, ModalSize } from "@via/utils/constants.tsx";
import { useDeploymentConfig } from "@via/pages/cortex/model-garden/model-garden.api.tsx";
import { DeployConfig } from "common";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";

interface AutoDeployConfigModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  aiModelId: number | undefined;
  currentConfig: DeployConfig | null;
  onConfirm: (config: DeployConfig | null) => void;
}

const columnHelper = createColumnHelper<DeployConfig | null>();

const AutoDeployConfigModal: FC<AutoDeployConfigModalProps> = ({
  open,
  setOpen,
  aiModelId,
  currentConfig,
  onConfirm,
}) => {
  const [selectedConfig, setSelectedConfig] = useState<DeployConfig | null>(
    null
  );

  const { data: deploymentConfigData, isLoading: isConfigLoading } =
    useDeploymentConfig(aiModelId, open && aiModelId !== undefined);

  // Memoize deploymentConfigs to prevent new array reference on each render
  const deploymentConfigs = useMemo(
    () => deploymentConfigData?.deploymentConfigs ?? [],
    [deploymentConfigData?.deploymentConfigs]
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      // Pre-select current config if it exists
      setSelectedConfig(currentConfig || null);
    } else {
      setSelectedConfig(null);
    }
  }, [open, currentConfig]);

  // Auto-select first config when data loads and no current config
  useEffect(() => {
    if (deploymentConfigs.length > 0 && !selectedConfig && !currentConfig) {
      const firstConfig = deploymentConfigs[0];
      if (firstConfig) {
        setSelectedConfig(firstConfig);
      }
    }
  }, [deploymentConfigs, selectedConfig, currentConfig]);

  const handleConfirmModal = async (): Promise<void> => {
    onConfirm(selectedConfig || null);
    setOpen(false);
  };

  const isConfigSelected = useCallback(
    (config: DeployConfig | null): boolean => {
      if (!config) return false;
      return (
        selectedConfig?.machineType === config.machineType &&
        selectedConfig?.acceleratorType === config.acceleratorType &&
        selectedConfig?.acceleratorCount === config.acceleratorCount &&
        selectedConfig?.containerImageURI === config.containerImageURI
      );
    },
    [selectedConfig]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: "select",
          cell: info => {
            const isSelected = isConfigSelected(info.row.original);
            return (
              <div className="flex items-center justify-center">
                <input
                  type="radio"
                  name="autoDeployConfig"
                  checked={isSelected}
                  onChange={() => setSelectedConfig(info.row.original)}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                />
              </div>
            );
          },
          header: () => "",
          enableSorting: false,
          size: 50,
          minSize: 50,
          maxSize: 50,
        }),
        columnHelper.accessor("machineType", {
          cell: info => {
            const value = info.row.original?.machineType;
            if (!value) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                {value || "-"}
              </span>
            );
          },
          header: () => <span>Machine Type</span>,
          enableSorting: false,
        }),
        columnHelper.accessor("acceleratorType", {
          cell: info => {
            const value = info.row.original?.acceleratorType;
            if (!value) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10">
                {value || "-"}
              </span>
            );
          },
          header: () => <span>Accelerator Type</span>,
          enableSorting: false,
          size: 150,
        }),
        columnHelper.accessor("acceleratorCount", {
          cell: info => {
            const value = info.row.original?.acceleratorCount;
            if (value === undefined || value === null) return null;
            return (
              <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-700/10">
                {value || "-"}
              </span>
            );
          },
          header: () => <span>Accelerator Count</span>,
          enableSorting: false,
          size: 50,
        }),
        columnHelper.accessor("containerImageURI", {
          cell: info => {
            const value = info.row.original?.containerImageURI;
            if (!value) return null;
            const rowIndex = info.row.index;
            return (
              <>
                <span
                  id={`auto-deploy-container-image-${rowIndex}`}
                  className="block max-w-[180px] truncate cursor-help">
                  {value || "-"}
                </span>
                <Tooltip
                  anchorSelect={`#auto-deploy-container-image-${rowIndex}`}
                  content={value || "-"}
                  place="top"
                  className="z-50"
                />
              </>
            );
          },
          header: () => <span>Container Image</span>,
          enableSorting: false,
          size: 200,
        }),
      ] as Array<ColumnDef<DeployConfig | null>>,
    [isConfigSelected]
  );

  const isConfirmDisabled = !selectedConfig || isConfigLoading;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      size={ModalSize.Large}
      width={800}
      title="Configure Auto Deploy"
      confirmText="Save Configuration"
      isConfirmDisabled={isConfirmDisabled}
      isLoading={isConfigLoading}
      onConfirm={handleConfirmModal}>
      <div className="flex flex-col gap-4 py-4">
        <p className="text-sm text-gray-600">
          Select a deployment configuration for automatic deployment. This
          configuration will be used when the model is automatically deployed.
        </p>

        {/* Deployment Configuration Selection */}
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
            Deployment Configuration <span className="text-red-500">*</span>
          </label>
          <Table
            isLoading={isConfigLoading}
            data={deploymentConfigs}
            columns={columns}
            showPagination={false}
            enableSorting={false}
            emptyMessage={EMPTY_TABLE_MESSAGE.DEPLOYMENT_CONFIGS}
            onRowClick={row => setSelectedConfig(row || null)}
            applyColumnSize={true}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AutoDeployConfigModal;
